import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ApiCallService from "../../Service/ApiCallService";
import { usePermission } from "../../permissions/usePermission";
import {
  canAccessDastakProgramDetails,
  fetchIsNecCouncilMember,
  NEC_COUNCIL_NAME,
} from "../../utils/necMembership";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

const DUPLICATE_INTEREST_MESSAGE =
  "You have already registered interest for this program, BJS HO will contact you soon.";
const ALL_CHAPTERS_INTEREST_MESSAGE =
  "You have already shown interest in this program through all your chapters, BJS HO will contact you soon.";

const DastakDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const userDetails = useSelector((state) => state.user?.userDetails);
  const { hasPolicyRole, isSuperAdmin } = usePermission();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isNecCouncilMember, setIsNecCouncilMember] = useState(false);
  const [chapters, setChapters] = useState([]);

  useEffect(() => {
    let active = true;

    const fetchMembership = async () => {
      const isNecMember = await fetchIsNecCouncilMember();
      if (active) {
        setIsNecCouncilMember(isNecMember);
      }
    };

    fetchMembership();

    return () => {
      active = false;
    };
  }, []);
  const [chapterLoading, setChapterLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openChapterPrompt, setOpenChapterPrompt] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [submittingChapterId, setSubmittingChapterId] = useState("");
  const [submittingProgramId, setSubmittingProgramId] = useState("");
  const [viewLoadingProgramId, setViewLoadingProgramId] = useState("");
  const [existingInterests, setExistingInterests] = useState([]);
  const [pendingChapterRequests, setPendingChapterRequests] = useState({
    create: [],
    join: [],
  });
  const [pendingRequestsLoading, setPendingRequestsLoading] = useState(false);


  const canOpenProgramDetails =
    canAccessDastakProgramDetails({
      userDetails,
      isSuperAdmin,
      hasPolicyRole,
    }) || isNecCouncilMember;

  const getStatusText = (status) => {
    if (!status) return "";
    if (typeof status === "object") {
      return `${status.status || status.name || status.label || status.value || ""}`.toLowerCase();
    }
    return `${status}`.toLowerCase();
  };

  const getEntityId = (entity) =>
    typeof entity === "object" ? entity?._id : entity;

  const normalizeApiArrayResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.data?.data?.data))
      return response.data.data.data;
    return [];
  };

  const hasPendingCreateRequest = pendingChapterRequests.create.length > 0;
  const hasPendingJoinRequest = pendingChapterRequests.join.length > 0;
  const hasPendingChapterRequest =
    hasPendingCreateRequest || hasPendingJoinRequest;

  const getPendingChapterRequestMessage = () => {
    if (hasPendingCreateRequest && hasPendingJoinRequest) {
      return "Your create chapter and join chapter requests are already pending for approval.";
    }
    if (hasPendingCreateRequest) {
      return "Your create new chapter request is already pending for approval.";
    }
    if (hasPendingJoinRequest) {
      return "Your join chapter request is already pending for approval.";
    }
    return "";
  };

  const getRequestUserId = (request) =>
    getEntityId(request?.userId) ||
    getEntityId(request?.user) ||
    getEntityId(request?.requestedBy) ||
    getEntityId(request?.createdBy);

  const requestHasUser = (request, userId) => {
    if (!request || !userId) return false;
    if (getRequestUserId(request) === userId) return true;
    const members = Array.isArray(request?.members) ? request.members : [];
    return members.some(
      (member) =>
        getEntityId(member?.userId) === userId ||
        getEntityId(member?.user) === userId,
    );
  };

  const isPendingStatus = (status) => {
    const statusText = getStatusText(status);
    return (
      !statusText ||
      statusText.includes("pending") ||
      statusText.includes("progress") ||
      statusText.includes("not approved") ||
      statusText.includes("not_approved") ||
      statusText.includes("requested")
    );
  };

  const fetchPendingChapterRequests = async () => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setPendingChapterRequests({ create: [], join: [] });
      return { create: [], join: [] };
    }

    try {
      setPendingRequestsLoading(true);
      const [createResponse, joinResponse] = await Promise.all([
        ApiCallService.get("/getNotApprovedChapters?page=1&limit=200"),
        ApiCallService.get("/chapter-member-requests"),
      ]);

      const createRequests = normalizeApiArrayResponse(createResponse).filter(
        (request) =>
          !getStatusText(request?.requestType).includes("edit") &&
          !request?.liveChapter &&
          isPendingStatus(request?.status) &&
          requestHasUser(request, userId),
      );
      const joinRequests = normalizeApiArrayResponse(joinResponse).filter(
        (request) =>
          isPendingStatus(request?.status) && requestHasUser(request, userId),
      );
      const pending = { create: createRequests, join: joinRequests };
      setPendingChapterRequests(pending);
      return pending;
    } catch (err) {
      console.error("Pending chapter request fetch error:", err);
      setPendingChapterRequests({ create: [], join: [] });
      message.error("Failed to check pending chapter requests.");
      return { create: [], join: [] };
    } finally {
      setPendingRequestsLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const data = await ApiCallService.get("/getDastakPrograms");

      if (data?.success) {
        setPrograms(data.data || []);
      } else {
        setPrograms([]);
      }
    } catch (err) {
      console.error("Programs fetch error:", err);
      message.error("Failed to fetch Dastak programs.");
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async () => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setChapters([]);
      message.error("User details not found. Please login again.");
      return [];
    }

    try {
      setChapterLoading(true);
      const data = await ApiCallService.get(`/chapter-members/user/${userId}`);
      const userChapters = normalizeApiArrayResponse(data);
      setChapters(userChapters);
      return userChapters;
    } catch (err) {
      console.error("Chapter fetch error:", err);
      setChapters([]);
      message.error("Failed to fetch your chapters.");
      return [];
    } finally {
      setChapterLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
    fetchInterests();
    fetchChapters();
  }, []);

  const handleShowInterestOpen = async (program) => {
    await fetchInterests(program);
    await fetchChapters();
    const programKey = program?._id || program?.name;
    setSubmittingProgramId(programKey);
    const interests = await fetchInterests();
    const userChapters = await fetchChapters();

    setSelectedProgram(program);

    if (userChapters.length > 1) {
      setSubmittingProgramId("");
      setOpenDialog(true);
      return;
    }

    const chapter = userChapters[0] || null;
    const chapterId = getEntityId(chapter?.chapterId) || null;

    if (hasProgramInterestForChapter(program, chapterId, interests)) {
      setSubmittingProgramId("");
      message.info(DUPLICATE_INTEREST_MESSAGE);
      return;
    }

    await handleShowInterest(chapter, program);
  };

  const handleViewProgramDetails = async (program) => {
    try {
      setViewLoadingProgramId(program?._id);

      if (canOpenProgramDetails) {
        navigate("../dastakProgramDetails", {
          state: {
            program,
          },
        });
        return;
      }

      const userChapters = normalizeApiArrayResponse(await fetchChapters());
      const canViewDetails =
        Array.isArray(userChapters) && userChapters.length > 0;

      if (canViewDetails) {
        navigate("../dastakProgramDetails", {
          state: {
            program,
          },
        });
        return;
      }

      setSelectedProgram(program);
      const pending = await fetchPendingChapterRequests();
      if (pending.create.length > 0 || pending.join.length > 0) {
        message.warning(
          pending.create.length > 0 && pending.join.length > 0
            ? "Your create chapter and join chapter requests are already pending for approval."
            : pending.create.length > 0
              ? "Your create new chapter request is already pending for approval."
              : "Your join chapter request is already pending for approval.",
        );
      }
      setOpenChapterPrompt(true);
    } finally {
      setViewLoadingProgramId("");
    }
  };

  const hasProgramInterestForChapter = (
    program,
    chapterId,
    interests = existingInterests,
  ) =>
    interests.some(
      (interest) =>
        interest?.programName === program?.name &&
        (getEntityId(interest?.chapterId) || null) === chapterId,
    );

  const isInterestDisabled = (chapterId) =>
    hasProgramInterestForChapter(
      selectedProgram,
      getEntityId(chapterId) || null,
    );

  const fetchInterests = async (program = null) => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setExistingInterests([]);
      return [];
    }

    try {
      const res = await ApiCallService.get("/program-interests");
      const allInterests = Array.isArray(res) ? res : res?.data || [];
      const userInterests = allInterests.filter(
        (interest) => getEntityId(interest?.userId) === userId,
      );

      setExistingInterests(userInterests);

      if (program) {
        return userInterests.filter(
          (interest) => interest?.programName === program?.name,
        );
      }

      return userInterests;
    } catch (err) {
      console.error("Interest fetch error:", err);
      return [];
    }
  };

  const handleShowInterest = async (chapter, programOverride) => {
    const userId = localStorage.getItem("userId");
    const chapterId = chapter?.chapterId;
    const program = programOverride || selectedProgram;
    const programName = program?.name;

    if (!userId || !programName) {
      message.error(
        "Unable to express interest. Missing user or program details.",
      );
      setSubmittingProgramId("");
      return;
    }

    try {
      if (chapterId) {
        setSubmittingChapterId(chapterId);
      } else {
        setSubmittingProgramId(program?._id || programName);
      }


      await ApiCallService.post("/program-interests", {
        userId,
        ...(chapterId ? { chapterId } : {}),
        programName,
      });
      await fetchInterests();
      message.success("Interest shown successfully.");
      setOpenDialog(false);
      // navigate("/dashboard")
    } catch (err) {
      const status = err?.response?.status;
      const apiMessage = err?.response?.data?.message;

      if (status === 409) {
        message.error(apiMessage || DUPLICATE_INTEREST_MESSAGE);
      } else {
        message.error(apiMessage || "Failed to express interest.");
      }
    } finally {
      setSubmittingChapterId("");
      setSubmittingProgramId("");
    }
  };

  return (
    <>
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          background: "#ffffff",
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: "#111827",
            fontSize: {
              xs: "1.5rem",
              sm: "2rem",
              md: "2rem",
            },
            textAlign:"center"
          }}
        >
          Dastak Programs
        </Typography>

        {/* Program Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // <=600
              sm: "repeat(2, 1fr)", // 600-900
              md: "repeat(3, 1fr)", // 900-1200
              lg: "repeat(5, 1fr)", // >1200
            },
            maxWidth:"1200px",
            justifySelf:"anchor-center",
            gap: 1,
          }}
        >
          {loading ? (
            <Box
              sx={{
                gridColumn: "1/-1",
                display: "flex",
                justifyContent: "center",
                py: 10,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            programs.map((program, index) => {
              const hasRegisteredInterest = chapters.length
                ? chapters.every((chapter) =>
                  hasProgramInterestForChapter(
                    program,
                    getEntityId(chapter?.chapterId)
                  )
                )
                : hasProgramInterestForChapter(program, null);

              return (
                <Box
                  key={program._id}
                  sx={{
                    background: "#f7f7f7",
                    border: "1px solid #E5E7EB",
                    borderRadius: 2,
                    overflow: "visible",
                    transition: "all .2s ease",

                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                      borderColor: "#D1D5DB",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: 225,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        width: "100%",
                        // aspectRatio: "1/1 !important",
                        background: "#fcfeff",
                        borderBottom: "1px solid #E5E7EB",
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        onClick={() => handleViewProgramDetails(program)}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          width: 36,
                          height: 36,
                          color: theme.palette.secondary.figma,

                          "&:hover": {
                            bgcolor: "#fff",
                          },
                        }}
                      >
                        {viewLoadingProgramId === program?._id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>

                      {program?.programPicture ? (
                        <Box
                          component="img"
                          src={program.programPicture}
                          alt={program.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Diversity3Icon
                          sx={{
                            fontSize: 80,
                            color: "#96ccff",
                          }}
                        />
                      )}
                    </Box>

                    {/* <Box
                      sx={{
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#fff",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          textAlign: "center",
                          lineHeight: 1.25,
                          fontSize: "1.75rem",
                        }}
                      >
                        {program.name}
                      </Typography>
                    </Box> */}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      minHeight: 40,
                    }}
                  >

                    <Box
                      onClick={() => {
                        if (hasRegisteredInterest) {
                          message.info(
                            chapters.length
                              ? ALL_CHAPTERS_INTEREST_MESSAGE
                              : DUPLICATE_INTEREST_MESSAGE,
                          );
                        }
                      }}
                      sx={{
                        cursor: hasRegisteredInterest
                          ? "not-allowed"
                          : "default",
                      }}
                    >
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled={
                          hasRegisteredInterest ||
                          submittingProgramId ===
                            (program?._id || program?.name)
                        }
                        onClick={() => handleShowInterestOpen(program)}
                        sx={{
                          py: 1.1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          pointerEvents: hasRegisteredInterest ? "none" : "auto",
                          background: theme.palette.primary.light,

                          "&:hover": {
                            background: theme.palette.secondary.figma,
                          },
                        }}
                      >
                        {submittingProgramId === (program?._id || program?.name)
                          ? "Saving..."
                          : hasRegisteredInterest
                            ? "Interest Registered"
                            : "Show Interest"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "#64748B",

              "&:hover": {
                background: "#F1F5F9",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            sx={{
              fontSize: "1.5rem",
              fontWeight: 700,
              color: "#111827",
              textAlign: "center",
            }}
          >
            Show Interest in{" "}
            <Box
              component="span"
              sx={{
                color: theme.palette.primary.main,
              }}
            >
              {selectedProgram?.name}
            </Box>
          </Typography>

       
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {chapterLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          ) : chapters.length > 0 ? (
            <>
              <Box
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                {chapters.map((chapter, index) => (
                  <Box
                    key={chapter.chapterId}
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: {
                        xs: "column",
                        sm: "row",
                      },
                      alignItems: {
                        xs: "flex-start",
                        sm: "center",
                      },
                      justifyContent: "space-between",
                      gap: 2,

                      borderBottom:
                        index !== chapters.length - 1
                          ? "1px solid #E5E7EB"
                          : "none",

                      "&:hover": {
                        background: "#FAFAFA",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.05rem",
                          color: "#111827",
                        }}
                      >
                        {chapter.chapterName}
                      </Typography>
                    </Box>

                  

                    <Button
                      variant="outlined"
                      disabled={
                        submittingChapterId === chapter.chapterId ||
                        isInterestDisabled(chapter.chapterId)
                      }
                      onClick={() => handleShowInterest(chapter)}
                      sx={{
                        minWidth: 150,
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        alignSelf: {
                          xs: "stretch",
                          sm: "center",
                        },
                      }}
                    >
                      {submittingChapterId === chapter.chapterId
                        ? "Saving..."
                        : isInterestDisabled(chapter.chapterId)
                          ? "Already Shown"
                          : "Show Interest"}
                    </Button>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                py: 6,
                textAlign: "center",
              }}
            >
              <Typography color="text.secondary">No chapters found.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openChapterPrompt}
        onClose={() => setOpenChapterPrompt(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            pr: 7,
            fontWeight: 700,
            color: "#111827",
          }}
        >
          Chapter membership required
          <IconButton
            onClick={() => setOpenChapterPrompt(false)}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "#64748B",

              "&:hover": {
                background: "#F1F5F9",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography color="text.secondary">
            To view Dastak program details, please join an existing chapter or
            create a new chapter.
          </Typography>

          {pendingRequestsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : hasPendingChapterRequest ? (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "#FFF7ED",
                border: "1px solid #FED7AA",
              }}
            >
              <Typography sx={{ color: "#9A3412", fontWeight: 600 }}>
                {getPendingChapterRequestMessage()}
              </Typography>
              <Typography sx={{ mt: 0.75, color: "#9A3412", fontSize: "0.9rem" }}>
                Please wait until approval is completed before submitting another chapter request.
              </Typography>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            gap: 1,
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            alignItems: "stretch",
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            disabled={pendingRequestsLoading || hasPendingJoinRequest}
            onClick={() => {
              setOpenChapterPrompt(false);
              navigate("../JoinExistingChapter");
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {hasPendingJoinRequest ? "Join Request Pending" : "Join Existing"}
          </Button>

          <Button
            fullWidth
            variant="contained"
            disabled={pendingRequestsLoading || hasPendingCreateRequest}
            onClick={() => {
              setOpenChapterPrompt(false);
              navigate("../createNewDastakChapter");
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {hasPendingCreateRequest ? "Create Request Pending" : "Create New"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DastakDashboard;
